import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Car,
  Users,
  FileText,
  Receipt,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { API } = useAuth();
  const [vehicleStats, setVehicleStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [contratosAtivos, setContratosAtivos] = useState(0);
  const [clientesAtivos, setClientesAtivos] = useState(0);
  const [recentContracts, setRecentContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Os números vêm calculados no banco (/dashboard/summary); a lista de
      // contratos serve só para mostrar os mais recentes.
      const [summaryRes, contractsRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { withCredentials: true }),
        axios.get(`${API}/contracts?status=ativo`, { withCredentials: true })
      ]);

      setVehicleStats(summaryRes.data.vehicles);
      setInvoiceStats(summaryRes.data.invoices);
      setContratosAtivos(summaryRes.data.contratos_ativos);
      setClientesAtivos(summaryRes.data.clientes_com_contrato_ativo);
      setRecentContracts(contractsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Valores grandes em formato curto: R$ 2,73 mi
  const formatCompact = (value) => {
    if (!value) return formatCurrency(0);
    if (value >= 1_000_000) {
      return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`;
    }
    return formatCurrency(value);
  };

  const plural = (n, singular, pluralForm) => `${n.toLocaleString('pt-BR')} ${n === 1 ? singular : pluralForm}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const emAtraso = invoiceStats?.vencidas || 0;
  const vencemHoje = invoiceStats?.vencem_hoje || 0;
  const cadastrados = vehicleStats?.total || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold font-[Manrope]">Bem-vindo ao GOLOC</h2>
        <p className="text-blue-100 mt-1">Sistema de Gestão de Locadora de Veículos</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Frota em operação (sem vendidos e roubados) */}
        <Card className="hover:shadow-md transition-shadow" data-testid="fleet-stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Frota em Operação</CardTitle>
            <Car className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{vehicleStats?.em_operacao || 0}</div>
            <div className="flex flex-col gap-1 mt-2 text-sm">
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                {vehicleStats?.alugados || 0} alugados · {vehicleStats?.disponiveis || 0} disponíveis
              </span>
              <span className="text-xs text-gray-400">
                {cadastrados} cadastrados · {vehicleStats?.vendidos || 0} vendidos · {vehicleStats?.roubados || 0} roubados
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Active Contracts */}
        <Card className="hover:shadow-md transition-shadow" data-testid="contracts-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Contratos Ativos</CardTitle>
            <FileText className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{contratosAtivos}</div>
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Users className="w-4 h-4" />
              <span>{plural(clientesAtivos, 'cliente ativo', 'clientes ativos')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Overdue invoices */}
        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          data-testid="overdue-invoices-card"
          onClick={() => navigate('/faturamento')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Em Atraso</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{formatCurrency(invoiceStats?.valor_vencidas)}</div>
            <div className="flex flex-col gap-1 mt-2 text-sm">
              <span className="text-red-500">
                {plural(emAtraso, 'fatura vencida', 'faturas vencidas')} (com multa)
              </span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                +{vencemHoje} {vencemHoje === 1 ? 'vence' : 'vencem'} hoje ({formatCurrency(invoiceStats?.valor_vencem_hoje)})
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Receivable in the next 30 days */}
        <Card className="hover:shadow-md transition-shadow" data-testid="pending-invoices-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">A Receber em 30 dias</CardTitle>
            <Receipt className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(invoiceStats?.valor_a_vencer_30d)}</div>
            <div className="flex flex-col gap-1 mt-2 text-sm">
              <span className="text-gray-500 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {plural(invoiceStats?.a_vencer_30d || 0, 'parcela', 'parcelas')}
              </span>
              <span className="text-xs text-gray-400">
                Total futuro: {formatCompact(invoiceStats?.valor_a_vencer_total)} em {plural(invoiceStats?.a_vencer || 0, 'parcela', 'parcelas')}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fleet Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fleet by Status */}
        <Card data-testid="fleet-status-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Status da Frota</span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/veiculos')}>
                Ver todos <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm">Disponíveis</span>
                </div>
                <span className="font-semibold">{vehicleStats?.disponiveis || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm">Alugados</span>
                </div>
                <span className="font-semibold">{vehicleStats?.alugados || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-sm">Reservados</span>
                </div>
                <span className="font-semibold">{vehicleStats?.reservados || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm">Manutenção</span>
                </div>
                <span className="font-semibold">{vehicleStats?.manutencao || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <span className="text-sm">Vendidos</span>
                </div>
                <span className="font-semibold">{vehicleStats?.vendidos || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-black"></div>
                  <span className="text-sm">Roubados</span>
                </div>
                <span className="font-semibold">{vehicleStats?.roubados || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Contracts */}
        <Card data-testid="recent-contracts-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Contratos Recentes
                {contratosAtivos > recentContracts.length && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    {recentContracts.length} de {contratosAtivos} ativos
                  </span>
                )}
              </span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/contratos')}>
                Ver todos <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentContracts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum contrato ativo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentContracts.map((contract) => (
                  <div
                    key={contract.contract_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/contratos')}
                  >
                    <div>
                      <p className="font-medium text-sm">{contract.client_name}</p>
                      <p className="text-xs text-gray-500">{contract.vehicle_info}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm">{formatCurrency(contract.valor_parcela)}</p>
                      <p className="text-xs text-gray-500">{contract.tipo_cobranca}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions-card">
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/clientes')}
              data-testid="quick-add-client"
            >
              <Users className="w-6 h-6" />
              <span>Novo Cliente</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/veiculos')}
              data-testid="quick-add-vehicle"
            >
              <Car className="w-6 h-6" />
              <span>Novo Veículo</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/contratos')}
              data-testid="quick-add-contract"
            >
              <FileText className="w-6 h-6" />
              <span>Novo Contrato</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/faturamento')}
              data-testid="quick-view-invoices"
            >
              <Receipt className="w-6 h-6" />
              <span>Faturamento</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
